// Admin-specific HTTP wrapper built on top of the existing api client.
//
// The base `api` from @/api already handles:
//   - X-Acting-Role injection from sessionStorage
//   - 401 → /signin redirect
//   - X-Request-ID surfacing
//   - JSON serialization / deserialization
//
// This module adds admin-specific concerns:
//   - If-Match header for optimistic concurrency (PATCH/DELETE)
//   - Typed error classification for 409 (role_in_use) and 412 (version_mismatch)
//   - 207 partial-reconcile response handling

import { request, ApiError } from "@/api";
import type { ApiRequestInit } from "@/api";
import type { AliasFailure, ReconcileSummary } from "@/types/role";

// ---------------------------------------------------------------------------
// Typed admin errors
// ---------------------------------------------------------------------------

/** 412 — the row's version has moved since the client last read it. */
export interface VersionMismatchError {
  kind: "version_mismatch";
  current_version: number;
  attempted_version: number;
  requestId: string | undefined;
}

/** 409 role_in_use — role still referenced by users or rules. */
export interface RoleInUseError {
  kind: "role_in_use";
  users_count: number;
  rules_count: number;
  requestId: string | undefined;
}

/** Discriminated union of admin-specific typed errors. */
export type AdminTypedError = VersionMismatchError | RoleInUseError;

export class AdminApiError extends Error {
  readonly typed: AdminTypedError;

  constructor(typed: AdminTypedError) {
    super(typed.kind);
    this.name = "AdminApiError";
    this.typed = typed;
  }
}

// ---------------------------------------------------------------------------
// 207 partial reconcile response shape
// ---------------------------------------------------------------------------

export interface PartialReconcileResult<T> {
  data: T;
  summary: ReconcileSummary;
  partial?: AliasFailure[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function readNumber(body: unknown, field: string): number {
  if (typeof body === "object" && body !== null && field in body) {
    const val = (body as Record<string, unknown>)[field];
    return typeof val === "number" ? val : 0;
  }
  return 0;
}

function readString(body: unknown, field: string): string {
  if (typeof body === "object" && body !== null && field in body) {
    const val = (body as Record<string, unknown>)[field];
    return typeof val === "string" ? val : "";
  }
  return "";
}

/**
 * Classify an ApiError into an AdminApiError when the status code matches
 * a known admin error pattern. Returns null if the error is not admin-specific.
 */
export function classifyAdminError(err: ApiError): AdminApiError | null {
  if (err.status === 412) {
    const code = readString(err.body, "error");
    if (code === "version_mismatch") {
      return new AdminApiError({
        kind: "version_mismatch",
        current_version: readNumber(err.body, "current_version"),
        attempted_version: readNumber(err.body, "attempted_version"),
        requestId: err.requestId,
      });
    }
  }

  if (err.status === 409) {
    const code = readString(err.body, "error");
    if (code === "role_in_use") {
      return new AdminApiError({
        kind: "role_in_use",
        users_count: readNumber(err.body, "users_count"),
        rules_count: readNumber(err.body, "rules_count"),
        requestId: err.requestId,
      });
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Core admin request functions
// ---------------------------------------------------------------------------

/**
 * Perform a GET request via the existing api client.
 */
export async function adminGet<T>(path: string, init?: ApiRequestInit): Promise<T> {
  const res = await request<T>(path, { ...init, method: "GET" });
  if (res === null) throw new ApiError(0, "aborted", "request aborted", undefined, null);
  return res.data;
}

/**
 * Perform a POST request via the existing api client.
 */
export async function adminPost<T>(
  path: string,
  body?: unknown,
  init?: ApiRequestInit,
): Promise<T> {
  const res = await request<T>(path, { ...init, method: "POST", body });
  if (res === null) throw new ApiError(0, "aborted", "request aborted", undefined, null);
  return res.data;
}

/**
 * Perform a PATCH request with If-Match header for optimistic concurrency.
 *
 * On 412 version_mismatch → throws AdminApiError.
 * On 207 partial reconcile → returns the body (caller inspects `partial`).
 * On other errors → rethrows the original ApiError.
 */
export async function adminPatch<T>(
  path: string,
  body: unknown,
  version: number,
  init?: ApiRequestInit,
): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set("If-Match", String(version));

  try {
    const res = await request<T>(path, {
      ...init,
      method: "PATCH",
      body,
      headers,
    });
    if (res === null) throw new ApiError(0, "aborted", "request aborted", undefined, null);
    return res.data;
  } catch (err) {
    if (err instanceof ApiError) {
      // 207 is treated as non-ok by the base client (it checks response.ok
      // which is true only for 200-299). However, 207 IS a success for us —
      // the row was persisted, some reconciles failed. The base client
      // throws an ApiError with status 207; we recover the body.
      if (err.status === 207) {
        return err.body as T;
      }
      const typed = classifyAdminError(err);
      if (typed) throw typed;
    }
    throw err;
  }
}

/**
 * Perform a DELETE request with If-Match header for optimistic concurrency.
 *
 * On 409 role_in_use → throws AdminApiError.
 * On 412 version_mismatch → throws AdminApiError.
 * On other errors → rethrows the original ApiError.
 */
export async function adminDelete<T>(
  path: string,
  version: number,
  init?: ApiRequestInit,
): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set("If-Match", String(version));

  try {
    const res = await request<T>(path, {
      ...init,
      method: "DELETE",
      headers,
    });
    if (res === null) throw new ApiError(0, "aborted", "request aborted", undefined, null);
    return res.data;
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 207) {
        return err.body as T;
      }
      const typed = classifyAdminError(err);
      if (typed) throw typed;
    }
    throw err;
  }
}
